import * as React from "react";
import { Stack, Box } from "@mui/material";
import { StatusCard } from "../StatusCard";
import { MetricCard } from "../MetricCard";
import { ProviderBadge } from "../ProviderBadge";
import { QueryStateBoundary } from "../QueryStateBoundary";
import { useBrokerList, useBrokerCapabilities } from "@/hooks/queries/useBrokerStatus";
import { useDashboardSystem } from "@/hooks/queries/useDashboardSystem";

interface BrokerSummary {
  provider_id: string;
  name: string;
  status: string;
  capabilities: string[];
}

interface CapabilitiesPayload {
  capabilities: Record<string, string[]>;
}

interface DashboardSystemPayload {
  providers: Array<{ provider_id: string; connection_status: string }>;
}

export function BrokerStatusSection(): React.JSX.Element {
  const brokersQuery = useBrokerList();
  const capabilitiesQuery = useBrokerCapabilities();
  const dashboardSystemQuery = useDashboardSystem();

  return (
    <StatusCard title="Broker Status">
      <Stack spacing={1.5}>
        <QueryStateBoundary
          query={brokersQuery}
          isEmpty={(data) => (data as unknown as BrokerSummary[]).length === 0}
          emptyMessage="No broker providers are configured."
        >
          {(payload) => {
            const brokers = payload as unknown as BrokerSummary[];
            return (
              <>
                <MetricCard
                  label="Configured Provider"
                  value={brokers.map((b) => b.name).join(", ")}
                />
                <Box>
                  <MetricCard
                    label="Provider Health"
                    render={
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {brokers.map((b) => (
                          <ProviderBadge
                            key={b.provider_id}
                            providerId={b.provider_id}
                            status={b.status}
                          />
                        ))}
                      </Stack>
                    }
                  />
                </Box>
              </>
            );
          }}
        </QueryStateBoundary>
        <QueryStateBoundary query={dashboardSystemQuery}>
          {(payload) => {
            const providers = (payload as unknown as DashboardSystemPayload).providers ?? [];
            const sessionSummary =
              providers.length > 0
                ? providers.map((p) => `${p.provider_id}: ${p.connection_status}`).join(", ")
                : null;
            return <MetricCard label="Session Status" value={sessionSummary} />;
          }}
        </QueryStateBoundary>
        <QueryStateBoundary
          query={capabilitiesQuery}
          isEmpty={(data) =>
            Object.keys((data as unknown as CapabilitiesPayload).capabilities ?? {}).length === 0
          }
          emptyMessage="No capabilities reported by the backend."
        >
          {(payload) => {
            const capabilities = (payload as unknown as CapabilitiesPayload).capabilities;
            const flattened = Object.entries(capabilities)
              .map(([providerId, caps]) => `${providerId}: ${caps.join(", ")}`)
              .join(" | ");
            return <MetricCard label="Available Capabilities" value={flattened} />;
          }}
        </QueryStateBoundary>
      </Stack>
    </StatusCard>
  );
}
